import { useVariant } from "@~/contexts/VariantContext/VariantContext";
import { CardComponentProps } from "@~/types/types";
import { PDPLink } from "../__shared__/PDPLink/PDPLink";
import { useEffect, useRef, useState } from "react";
import { getDeviceCapabilities } from "./utils";
import styles from "./ImageList.module.scss";
import { Image } from "../__shared__/Image/Image";

export const ImageList = ({
  index,
  product,
}: Pick<CardComponentProps, "index" | "product">) => {
  const { variant } = useVariant();
  const variantHasAlternativeImages =
    variant?.images?.alternatives && variant.images.alternatives.length > 0;
  const carouselElement = useRef<HTMLAnchorElement | null>(null);
  const [displayAllAlternateImages, setDisplayAllAlternateImages] =
    useState(false);

  useEffect(() => {
    if (carouselElement?.current && variantHasAlternativeImages) {
      const carouselElementCurrent = carouselElement.current;
      const isHoverDevice = getDeviceCapabilities().hasHover;

      if (isHoverDevice) {
        const handleMouseEnter = () => {
          if (!displayAllAlternateImages) setDisplayAllAlternateImages(true);
          hoverPrimaryImage(true);
        };
        const handleMouseLeave = () => hoverPrimaryImage(false);

        carouselElementCurrent.addEventListener("mouseenter", handleMouseEnter);
        carouselElementCurrent.addEventListener("mouseleave", handleMouseLeave);

        return () => {
          if (carouselElement?.current) {
            carouselElementCurrent.removeEventListener(
              "mouseenter",
              handleMouseEnter,
            );
            carouselElementCurrent.removeEventListener(
              "mouseleave",
              handleMouseLeave,
            );
          }
        };
      }
    }

    return () => {};
  }, [variantHasAlternativeImages]);

  useEffect(() => {
    const updateImages = () => setDisplayAllAlternateImages(true);
    if (carouselElement?.current && variantHasAlternativeImages) {
      const isTouchDevice = getDeviceCapabilities().hasTouch;

      if (isTouchDevice) {
        carouselElement.current.addEventListener("touchstart", updateImages, {
          once: true,
        });

        return () => {
          if (carouselElement?.current) {
            carouselElement.current.removeEventListener(
              "touchstart",
              updateImages,
            );
          }
        };
      }
    }

    return () => {};
  }, [variantHasAlternativeImages]);

  const isFirstCard = index === 0;
  useEffect(() => {
    if (isFirstCard && getDeviceCapabilities().hasTouch) {
      carouselElement.current?.classList.add(styles.imageList__preview!);
    }
  }, [isFirstCard]);

  if (!variant?.images.primary) {
    return null;
  }

  const primaryImgUrl = variant?.images.primary.split("?")[0] || "";
  const altImageUrls = variant?.images?.alternatives || [];

  const hoverPrimaryImage = (slide: boolean) => {
    if (carouselElement?.current) {
      const firstImageDisplayed =
        carouselElement.current.scrollLeft <=
        carouselElement.current.clientWidth;
      if (firstImageDisplayed) {
        const targetScrollLeft = slide
          ? carouselElement.current.clientWidth
          : 0;
        carouselElement.current.scrollLeft = targetScrollLeft;
      }
    }
  };

  return (
    <PDPLink
      product={product}
      componentName="ImageList"
      className={styles.imageList}
      testId="image-list"
      ref={carouselElement}
      ariaLabel="product image carousel"
    >
      <div className={styles.imageListItem}>
        <Image
          url={primaryImgUrl}
          title={product.title}
          colour={variant?.colour?.colour || ""}
          index={index}
        />
      </div>
      {variantHasAlternativeImages && altImageUrls[0] && (
        <div className={styles.imageListItem} key={altImageUrls[0]}>
          <Image
            url={altImageUrls[0]}
            title={`${product.title} - view 2`}
            colour={variant?.colour?.colour || ""}
            index={index}
          />
        </div>
      )}
      {variantHasAlternativeImages &&
        displayAllAlternateImages &&
        altImageUrls.length > 1 &&
        altImageUrls.slice(1).map((url) => {
          return (
            <div className={styles.imageListItem} key={url}>
              <Image
                url={url}
                title={`${product.title} - view ${index + 3}`}
                colour={variant?.colour?.colour || ""}
                index={index}
              />
            </div>
          );
        })}
    </PDPLink>
  );
};
